import React from "react";
import {daysUntilExpiry, formatAmount, formatDate} from "../contractExplorerTypes.ts";
import type {ContractDetailApi} from "../useContractDetailQuery.ts";

/** Payment-term columns worth showing; the full schema_v3 sheet comes later. */
const TERM_FIELDS: {label: string; key: 'term_type' | 'net_days_value' | 'payment_method' | 'term_details'}[] = [
    {label: 'Type', key: 'term_type'},
    {label: 'Net days', key: 'net_days_value'},
    {label: 'Method', key: 'payment_method'},
    {label: 'Details', key: 'term_details'},
];

/** '—' for none, the code for one, "EUR, GBP (mixed)" for a mixed contract. */
const contractCurrencyLabel = (codes: string[]): string => {
    if (codes.length === 0) return '—';
    if (codes.length === 1) return codes[0];
    return `${codes.join(', ')} (mixed)`;
};

type Props = {
    data: ContractDetailApi;
};

/** Contract summary + items + payment terms (the modal header carries the
 *  title and status chips). */
export const ContractDetailContent: React.FC<Props> = ({data}) => {
    const daysLeft = daysUntilExpiry(data.expiration_date, data.as_of);

    return (
        <>
            <div className="cd-summary">
                <dl className="cd-facts">
                    <div><dt>Supplier</dt><dd>{data.supplier_name || '—'}</dd></div>
                    <div>
                        <dt>Contracted amount</dt>
                        <dd>{formatAmount(data.contracted_amount ?? 0, data.currency)}</dd>
                    </div>
                    <div>
                        <dt>Contract currency</dt>
                        <dd>{contractCurrencyLabel(data.contract_currencies)}</dd>
                    </div>
                    <div><dt>Effective</dt><dd>{formatDate(data.effective_date)}</dd></div>
                    <div>
                        <dt>Expires</dt>
                        <dd>
                            {formatDate(data.expiration_date)}
                            {data.lifecycle === 'expiring' && daysLeft !== null &&
                                <span className="ce-expiry-hint"> · in {daysLeft}d</span>}
                        </dd>
                    </div>
                    <div><dt>Parsed type</dt><dd>{data.contract_type || '—'}</dd></div>
                    <div><dt>Document</dt><dd className="cd-path">{data.file_name || '—'}</dd></div>
                </dl>
                {data.changed_columns.length > 0 && (
                    <p className="cd-note">Changed from the extraction: {data.changed_columns.join(', ')}</p>
                )}
            </div>

            <h2 className="cd-section">Items ({data.items.length})</h2>
            {data.items.length === 0
                ? <p className="cd-empty">The extraction found no priced items for this contract.</p>
                : (
                    <div className="cd-table-scroll">
                        <table className="cd-table">
                            <thead>
                                <tr>
                                    <th>Item</th><th>Qty</th><th>Unit</th>
                                    <th className="cd-num">Unit price</th>
                                    <th className="cd-num">Net</th>
                                    <th className="cd-num">Gross</th>
                                    <th>Currency</th>
                                    <th className="cd-num">Spend ({data.currency})</th>
                                    <th>Billing</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map(item => (
                                    <tr key={item.id}>
                                        <td>
                                            {item.item_name}
                                            {item.item_description &&
                                                <span className="cd-sub">{item.item_description}</span>}
                                        </td>
                                        <td>{item.item_quantity ?? '—'}</td>
                                        <td>{item.item_unit || '—'}</td>
                                        <td className="cd-num">{formatAmount(item.item_unit_price ?? 0)}</td>
                                        <td className="cd-num">{formatAmount(item.item_spend_net ?? 0)}</td>
                                        <td className="cd-num">{formatAmount(item.item_spend_gross ?? 0)}</td>
                                        <td>{item.item_price_currency_clean || item.item_price_currency || '—'}</td>
                                        <td className="cd-num">{formatAmount(item.item_spend ?? 0)}</td>
                                        <td>
                                            {[item.payment_frequency, item.pricing_model].filter(Boolean).join(' / ') || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <h2 className="cd-section">Payment terms ({data.payment_terms.length})</h2>
            {data.payment_terms.length === 0
                ? <p className="cd-empty">The extraction found no payment terms for this contract.</p>
                : (
                    <div className="cd-table-scroll">
                        <table className="cd-table">
                            <thead>
                                <tr>{TERM_FIELDS.map(f => <th key={f.key}>{f.label}</th>)}</tr>
                            </thead>
                            <tbody>
                                {data.payment_terms.map(term => (
                                    <tr key={term.id}>
                                        {TERM_FIELDS.map(f => <td key={f.key}>{term[f.key] || '—'}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
        </>
    );
};
